import React from "react";
import { Link } from "react-router-dom";
import { 
  Check, ArrowRight, ArrowLeft, Building2, Target, Globe, 
  TrendingUp, BarChart3, Users, Sparkles, Crown
} from "lucide-react";

const Accompagnement360Page = () => {
  const services = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Définir votre positionnement",
      desc: "Stratégie de marque unique et différenciante adaptée à votre marché."
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Déployer votre identité",
      desc: "Sur l'ensemble de vos points de contact : digital, print, événementiel."
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Piloter vos campagnes",
      desc: "Ads, automation, contenu - nous gérons l'ensemble de votre acquisition."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Mesurer et optimiser",
      desc: "Indicateurs de performance dédiés et optimisation continue des résultats."
    }
  ];

  const benefits = [
    "Un partenaire stratégique dédié à votre succès",
    "Vision à long terme et cohérence sur tous les canaux",
    "Indicateurs de performance personnalisés",
    "Flexibilité et réactivité maximales",
    "Équipe pluridisciplinaire à votre service"
  ];

  const forWho = [
    { icon: <Building2 className="w-6 h-6" />, text: "Grands comptes et ETI" },
    { icon: <TrendingUp className="w-6 h-6" />, text: "Entreprises en forte croissance" },
    { icon: <Users className="w-6 h-6" />, text: "Organisations multi-sites" },
    { icon: <Globe className="w-6 h-6" />, text: "Entreprises à ambition internationale" }
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden" data-testid="accompagnement-hero">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-20 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-purple-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link 
            to="/offres" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
            data-testid="back-to-offres"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour aux offres
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium mb-6">
                <Crown className="w-4 h-4" />
                Offre Premium
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Accompagnement 360° Sur Mesure
              </h1>
              <p className="text-xl text-white/90 mb-4 font-medium">
                Une ambition forte ? Une stratégie de marque globale à déployer ?
              </p>
              <p className="text-lg text-white/70 mb-8">
                Nous prenons en main votre communication dans son intégralité. De la stratégie de marque à l'exécution des campagnes, nous devenons votre partenaire privilégié.
              </p>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4 inline-block mb-8">
                <span className="text-white/70 text-sm">Offre sur mesure</span>
                <div className="text-white font-semibold mt-1">Tarif adapté à vos besoins</div>
              </div>
              
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 text-white font-semibold rounded-full hover:bg-cyan-400 transition-all duration-300 shadow-xl"
                data-testid="cta-accompagnement"
              >
                Prenons rendez-vous
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                alt="Équipe stratégique"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-gray-900">Partenaire</div>
                    <div className="text-gray-600 text-sm">Privilégié & dédié</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ce que nous prenons en charge */}
      <section className="py-20 bg-white" data-testid="accompagnement-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">Notre engagement</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-4">
              Ce que nous prenons en charge
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Une prise en main complète de votre communication pour vous permettre de vous concentrer sur votre cœur de métier.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
                data-testid={`service-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-gray-900 text-cyan-400 flex items-center justify-center mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pour qui */}
      <section className="py-20 bg-gray-50" data-testid="accompagnement-pour-qui">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">Idéal pour</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-6">
                Les entreprises à forte ambition
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                Cette offre premium est conçue pour les organisations qui souhaitent déléguer entièrement leur stratégie de communication à un partenaire de confiance.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {forWho.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm">
                    <div className="w-10 h-10 rounded-lg bg-cyan-100 text-cyan-600 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <span className="font-medium text-gray-900 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gray-900 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">Le résultat</h3>
              <p className="text-gray-300 mb-8 text-lg">
                Une marque puissante, cohérente et performante, portée par une vision à long terme.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-white/90">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800" data-testid="accompagnement-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium mb-6">
            <Crown className="w-4 h-4" />
            Offre Premium
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à passer au niveau supérieur ?
          </h2>
          <p className="text-white/70 text-lg mb-10">
            Discutons de vos ambitions et définissons ensemble une stratégie sur mesure pour votre entreprise.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-cyan-500 text-white font-semibold rounded-full hover:bg-cyan-400 transition-all duration-300 shadow-lg"
              data-testid="cta-contact-accompagnement"
            >
              Prenons rendez-vous
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/offres"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300"
            >
              Voir toutes nos offres
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Accompagnement360Page;
